import React, { useState } from "react";
import { ApiAdminUser } from "utils/api/admin/user";
import { formatDateString } from "utils/date";
import { formatCurrency, snakeCaseToTitleCase } from "utils/string";
import { getAdminUserPlaidTransactions } from "store/admin/api/apiSlice";
import Button, { ButtonLevel, ButtonSize } from "components/ui/Button";
import { Heading } from "components/ui/Text";
import * as Styled from "./styled";
import Table from "../../components/Table";

const HEADINGS = [
  "Date",
  "Amount",
  "Category",
  "Category ID",
  "Merchant",
  "Name",
  "Type",
];

type FilterType = "all" | "expenses" | "deposits";

interface FilterOption {
  label: string;
  filterFn: (transaction: any) => boolean;
}

const FILTER_OPTIONS: Record<FilterType, FilterOption> = {
  all: {
    label: "All",
    filterFn: () => true,
  },
  expenses: {
    label: "Expenses",
    filterFn: (transaction) => transaction.amount > 0,
  },
  deposits: {
    label: "Direct Deposits",
    filterFn: (transaction) => transaction.amount < 0,
  },
};

interface Props {
  user: ApiAdminUser;
}

const PlaidTransactions: React.FC<Props> = ({ user }) => {
  const [showTransactions, setShowTransactions] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");
  const { data } = getAdminUserPlaidTransactions(user.id, {
    skip: !showTransactions,
  });

  const { plaidTransactions: transactions } = data || {};

  const filteredTransactions = transactions
    ? transactions.filter(FILTER_OPTIONS[filter].filterFn)
    : [];

  return (
    <Styled.SectionTile>
      <Styled.Header>
        <Styled.HeaderSection>
          <Heading.H4 tag="h2">Plaid Transactions</Heading.H4>
        </Styled.HeaderSection>

        {showTransactions && (
          <Styled.HeaderSection>
            {Object.entries(FILTER_OPTIONS).map(([filterType, option]) => (
              <Styled.HeaderFilter
                key={filterType}
                onClick={() => setFilter(filterType as FilterType)}
                size={ButtonSize.sm}
                level={ButtonLevel.ghost}
                $isActive={filter === filterType}
              >
                {option.label}
              </Styled.HeaderFilter>
            ))}
          </Styled.HeaderSection>
        )}
      </Styled.Header>

      <div>
        <Table>
          <thead>
            <tr>
              {HEADINGS.map((heading) => (
                <th key={heading}>{heading}</th>
              ))}
            </tr>
          </thead>

          {showTransactions && (
            <tbody>
              {filteredTransactions.map((transaction, index) => (
                <tr key={index} data-heading={HEADINGS[index]}>
                  <td>
                    {formatDateString(transaction.date, "numericShortWithTime")}
                  </td>
                  <td>{formatCurrency(transaction.amount, true)}</td>
                  <td>{transaction.category}</td>
                  <td>{transaction.categoryId}</td>
                  <td>{transaction.merchantName}</td>
                  <td>{transaction.name}</td>
                  <td>{snakeCaseToTitleCase(transaction.transactionType)}</td>
                </tr>
              ))}
            </tbody>
          )}
        </Table>

        {!showTransactions && (
          <Styled.TableButtonWrapper>
            <Button
              onClick={() => setShowTransactions(true)}
              size={ButtonSize.md}
              level={ButtonLevel.ghost}
            >
              Show Transactions
            </Button>
          </Styled.TableButtonWrapper>
        )}
      </div>
    </Styled.SectionTile>
  );
};

export default PlaidTransactions;
